import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import {
  Button,
  Box,
  Container,
  TextField,
  Typography,
  Grid,
  Select,
  MenuItem,
  IconButton,
  Stack,
  LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useForm, Controller } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { POST_SERVER, ORGANIZATION_SERVER, CODE_SERVER } from '../../Config';
import axios from 'axios';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
axios.defaults.withCredentials = true;

const CustomTitleTypography = styled(Typography)({
  color: '#424242',
  fontSize: '14px',
  fontWeight: 600,
  minWidth: '130px',
  textAlign: 'left',
});
const CustomSubtitleTypography = styled(Typography)({
  color: '#9E9E9E',
  fontSize: '11px',
  marginBottom: '2px',
});
const CustomSection = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '16px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  border: '1px solid #e8e8e8',
  marginBottom: '16px',
}));
const SectionTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 700,
  color: '#1f2937',
  marginBottom: '12px',
  paddingBottom: '10px',
  borderBottom: '2px solid #e5e7eb',
});
const CustomFormRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  paddingTop: '8px',
  paddingBottom: '8px',
  borderBottom: '1px solid #f5f5f5',
  '&:last-child': { borderBottom: 'none' },
});
const FileUploadContainer = styled(Box)(({ theme }) => ({
  border: '2px dashed #d1d5db',
  borderRadius: '10px',
  padding: '16px',
  backgroundColor: '#f8fafc',
  minHeight: '200px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  gap: '10px',
  overflowY: 'auto',
}));
const FileItem = styled(Box)(({ theme }) => ({
  padding: '6px 8px',
  backgroundColor: 'white',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  boxSizing: 'border-box',
}));
const FileNameText = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#374151',
  wordBreak: 'break-all',
  lineHeight: 1.3,
});
const ProgressContainer = styled(Box)({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const isMetadataFile = (name) => {
  const lower = name.toLowerCase();
  return lower.includes('metadata') && (lower.includes('.xlsx') || lower.includes('.xls'));
};

const AddPost = (props) => {
  const [domainList, setDomainList] = useState([]);
  const [dataTargetList, setDataTargetList] = useState([]);
  const [dataTypeList, setDataTypeList] = useState([]); // ← Domain 연동으로 동적 관리
  const [accessLevelList, setAccessLevelList] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [addFileList, setAddFileList] = useState([]);
  const [uploadProgresses, setUploadProgresses] = useState([]);
  const [tempFileMap, setTempFileMap] = useState([]);
  const tempFileMapRef = useRef([]);

  const fileInput = useRef();
  const { state } = useLocation();
  const navigate = useNavigate();
  const user = useSelector((s) => s.user.userData);

  const modules = {
    toolbar: {
      container: [
        [{ font: [] }],
        [{ size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }, { align: [] }],
        ['link', 'code-block'],
        ['clean'],
      ],
    },
  };

  const { handleSubmit, register, setValue, watch, control } = useForm({
    defaultValues: {
      collectionPeriod: { startDate: null, endDate: null },
      groupId: state?.group ?? '',
      domain: '',
      dataTarget: '',
      dataType: '',
      sampleCount: '',
      researchDirector: '',
      accessLevel: '',
      collectionMethod: '',
    },
  });

  const updateTempFileMap = (updater) => {
    setTempFileMap((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      tempFileMapRef.current = next;
      return next;
    });
  };

  // Domain watch → DataType 동적 조회
  const watchedDomain = watch('domain');

  useEffect(() => {
    if (!watchedDomain) {
      setDataTypeList([]);
      return;
    }
    axios
      .get(`${process.env.REACT_APP_SERVER}${CODE_SERVER}/codecode/${watchedDomain}`)
      .then((res) => setDataTypeList(res.data))
      .catch(() => setDataTypeList([]));
  }, [watchedDomain]);

  // ── 파일 임시 업로드 핸들러
  const handleFileInputChange = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const newProgresses = files.map((file) => ({
      id: `${file.name}_${Date.now()}_${Math.random()}`,
      fileName: file.name,
      progress: 0,
      status: 'pending',
    }));
    setUploadProgresses((prev) => [...prev, ...newProgresses]);
    setAddFileList((prev) => [...prev, ...files]);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const progressId = newProgresses[i].id;
      const formData = new FormData();
      formData.append('files', file);
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setUploadProgresses((prev) =>
            prev.map((p) => (p.id === progressId ? { ...p, progress: percent, status: 'uploading' } : p))
          );
        }
      });

      xhr.addEventListener('load', () => {
        const status = xhr.status === 200 ? 'done' : 'error';
        setUploadProgresses((prev) => {
          if (!prev.find((p) => p.id === progressId)) return prev;
          return prev.map((p) => (p.id === progressId ? { ...p, status } : p));
        });

        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.ok && Array.isArray(response.files) && response.files.length > 0) {
              const serverName = response.files[0];
              setUploadProgresses((currentProgresses) => {
                const exists = currentProgresses.find((p) => p.id === progressId);
                if (!exists) {
                  axios
                    .delete(`${process.env.REACT_APP_SERVER}${POST_SERVER}/temp-delete/${serverName}`)
                    .catch((err) => console.error('Auto cleanup failed:', err));
                } else {
                  updateTempFileMap((prev) => {
                    if (prev.find((f) => f.id === progressId)) return prev;
                    return [...prev, { id: progressId, tempName: serverName, originalName: file.name }];
                  });
                }
                return currentProgresses;
              });
            }
          } catch (err) {
            console.error('Response parse error:', err);
          }
        }
      });

      xhr.open('POST', `${process.env.REACT_APP_SERVER}${POST_SERVER}/upload-temp`);
      xhr.send(formData);
    }
    event.target.value = '';
  };

  // ── 임시 파일 개별 삭제
  const removeTempFile = async (progressId) => {
    const target = tempFileMapRef.current.find((f) => f.id === progressId);
    const targetProgress = uploadProgresses.find((p) => p.id === progressId);

    if (target) {
      try {
        await axios.delete(`${process.env.REACT_APP_SERVER}${POST_SERVER}/temp-delete/${target.tempName}`);
      } catch (err) {
        console.error('Temp delete failed:', err);
      }
    }

    updateTempFileMap((prev) => prev.filter((f) => f.id !== progressId));
    setUploadProgresses((prev) => prev.filter((p) => p.id !== progressId));
    setAddFileList((prev) => {
      let removed = false;
      return prev.filter((file) => {
        if (!removed && file.name === targetProgress?.fileName) {
          removed = true;
          return false;
        }
        return true;
      });
    });
  };

  // ── 폼 제출
  const onSubmit = async (data) => {
    if (uploadProgresses.some((p) => p.status === 'uploading')) {
      enqueueSnackbar('파일 업로드 중입니다. 잠시 후 다시 시도하세요.', { variant: 'warning' });
      return;
    }

    if (tempFileMap.length > 0) {
      const hasMetadata = tempFileMap.some((f) => isMetadataFile(f.originalName));
      if (!hasMetadata) {
        enqueueSnackbar('Metadata 엑셀 파일이 없으면 저장할 수 없습니다.', { variant: 'error' });
        return;
      }
    }

    const formData = new FormData();
    const { _id: publisherId, name: publisherName, role } = user;
    formData.append('publisherId', publisherId);
    formData.append('publisherName', publisherName);
    formData.append('collectionPeriod', JSON.stringify(data.collectionPeriod));
    formData.append('domain', data.domain);
    formData.append('dataTarget', data.dataTarget);
    formData.append('dataType', data.dataType);
    formData.append('sampleCount', data.sampleCount);
    formData.append('researchDirector', data.researchDirector);
    formData.append('accessLevel', data.accessLevel);
    formData.append('collectionMethod', data.collectionMethod);
    formData.append('status', true);

    const resolvedGroup =
      role === 'user' ? groupList.find((v) => v._id === state.group) : groupList.find((v) => v._id === data.groupId);
    formData.append('groupId', resolvedGroup?._id || '');
    formData.append('groupName', resolvedGroup?.name || '');

    tempFileMap.forEach(({ tempName, originalName }) => {
      formData.append('tempFiles', tempName);
      formData.append('originalFileNames', originalName);
    });

    try {
      const res = await axios.post(`${process.env.REACT_APP_SERVER}${POST_SERVER}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.status >= 200 && res.status < 300) {
        enqueueSnackbar('New Post Add Completed', { variant: 'success', autoHideDuration: 2500 });
        navigate(-1);
      }
    } catch (error) {
      console.error('서버 응답 에러:', error.response?.data);
      enqueueSnackbar('Upload failed', { variant: 'error' });
    }
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${process.env.REACT_APP_SERVER}${ORGANIZATION_SERVER}/list`),
      axios.get(`${process.env.REACT_APP_SERVER}${CODE_SERVER}/codecode/10`),
      axios.get(`${process.env.REACT_APP_SERVER}${CODE_SERVER}/codecode/20`),
      // codecode/30 제거 → watchedDomain useEffect에서 동적 조회
      axios.get(`${process.env.REACT_APP_SERVER}${CODE_SERVER}/codecode/40`),
    ]).then(([grpRes, domRes, tgtRes, lvlRes]) => {
      setGroupList(grpRes.data);
      setDomainList(domRes.data);
      setDataTargetList(tgtRes.data);
      setAccessLevelList(lvlRes.data);
      if (state?.group) setValue('groupId', state.group);
    });
  }, []);

  const isUploading = uploadProgresses.some((p) => p.status === 'uploading');

  return (
    <div className="ProjectPage">
      <Container maxWidth="lg" sx={{ pt: '160px', pb: '60px' }}>
        {/* ── 헤더 ── */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            p: 2,
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: '10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              새 데이터 등록
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="outlined" onClick={() => navigate(-1)} sx={{ textTransform: 'none', borderRadius: '6px' }}>
              취소
            </Button>
            <Button
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={handleSubmit(onSubmit)}
              disabled={isUploading}
              sx={{ textTransform: 'none', borderRadius: '6px', px: 3, boxShadow: '0 4px 12px rgba(59,130,246,0.4)' }}
            >
              저장
            </Button>
          </Box>
        </Box>

        {/* ── 섹션 1+2: 기본정보(좌) + 분류정보(우) 2열 레이아웃 ── */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* 좌측: 기본 정보 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomSection sx={{ mb: 0, height: '100%', boxSizing: 'border-box' }}>
              <SectionTitle>기본 정보</SectionTitle>

              {user?.role !== 'user' && (
                <CustomFormRow>
                  <CustomTitleTypography>그룹</CustomTitleTypography>
                  <Box sx={{ flex: 1 }}>
                    <Controller
                      control={control}
                      name="groupId"
                      render={({ field }) => (
                        <Select {...field} fullWidth size="small" value={groupList.length > 0 ? field.value : ''}>
                          {groupList.map((v, idx) => (
                            <MenuItem key={`group-${idx}`} value={v._id}>
                              {v.name}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </Box>
                </CustomFormRow>
              )}

              <CustomFormRow>
                <CustomTitleTypography>수집 기간</CustomTitleTypography>
                <Box sx={{ flex: 1 }}>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        control={control}
                        name="collectionPeriod.startDate"
                        render={({ field: { value, onChange } }) => (
                          <TextField
                            fullWidth
                            size="small"
                            label="시작일"
                            type="date"
                            value={value ? new Date(value).toISOString().split('T')[0] : ''}
                            onChange={(e) => onChange(e.target.value ? new Date(e.target.value) : null)}
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        control={control}
                        name="collectionPeriod.endDate"
                        render={({ field: { value, onChange } }) => (
                          <TextField
                            fullWidth
                            size="small"
                            label="종료일"
                            type="date"
                            value={value ? new Date(value).toISOString().split('T')[0] : ''}
                            onChange={(e) => onChange(e.target.value ? new Date(e.target.value) : null)}
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </CustomFormRow>

              <CustomFormRow>
                <CustomTitleTypography>연구 책임자</CustomTitleTypography>
                <Box sx={{ flex: 1 }}>
                  <TextField fullWidth size="small" placeholder="연구 책임자 입력" {...register('researchDirector')} />
                </Box>
              </CustomFormRow>

              <CustomFormRow>
                <CustomTitleTypography>샘플 수</CustomTitleTypography>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="샘플 수 입력"
                    inputProps={{ min: 0 }}
                    onKeyDown={(e) => {
                      if (e.key === '-' || e.key === '+' || e.key === 'e') {
                        e.preventDefault();
                      }
                    }}
                    {...register('sampleCount', {
                      min: { value: 0, message: '0 이상의 값을 입력하세요' },
                      onChange: (e) => {
                        if (Number(e.target.value) < 0) setValue('sampleCount', 0);
                      },
                    })}
                  />
                </Box>
              </CustomFormRow>
            </CustomSection>
          </Grid>

          {/* 우측: 분류 정보 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomSection sx={{ mb: 0, height: '100%', boxSizing: 'border-box' }}>
              <SectionTitle>분류 정보</SectionTitle>

              <CustomFormRow>
                <Box sx={{ mb: 0.5 }}>
                  <CustomTitleTypography>Domain</CustomTitleTypography>
                  <CustomSubtitleTypography>데이터 도메인을 선택하세요</CustomSubtitleTypography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Controller
                    control={control}
                    name="domain"
                    render={({ field }) => (
                      <Select
                        {...field}
                        fullWidth
                        size="small"
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          setValue('dataType', ''); // DataType 초기화
                        }}
                      >
                        {domainList.map((v, idx) => (
                          <MenuItem key={`domain-${idx}`} value={v.code}>
                            {v.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </Box>
              </CustomFormRow>

              <CustomFormRow>
                <Box sx={{ mb: 0.5 }}>
                  <CustomTitleTypography>Data Target</CustomTitleTypography>
                  <CustomSubtitleTypography>데이터 대상을 선택하세요</CustomSubtitleTypography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Select fullWidth size="small" value={watch('dataTarget')} {...register('dataTarget')}>
                    {dataTargetList.map((v, idx) => (
                      <MenuItem key={`target-${idx}`} value={v.code}>
                        {v.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </CustomFormRow>

              {/* Domain 미선택 시 비활성 */}
              <CustomFormRow>
                <Box sx={{ mb: 0.5 }}>
                  <CustomTitleTypography>Data Type</CustomTitleTypography>
                  <CustomSubtitleTypography>데이터 유형을 선택하세요</CustomSubtitleTypography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Controller
                    control={control}
                    name="dataType"
                    render={({ field }) => (
                      <Select {...field} fullWidth size="small" disabled={!watchedDomain} displayEmpty>
                        {!watchedDomain && (
                          <MenuItem value="" disabled>
                            Domain을 먼저 선택하세요
                          </MenuItem>
                        )}
                        {dataTypeList.map((v, idx) => (
                          <MenuItem key={`type-${idx}`} value={v.code}>
                            {v.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </Box>
              </CustomFormRow>

              <CustomFormRow>
                <Box sx={{ mb: 0.5 }}>
                  <CustomTitleTypography>Access Level</CustomTitleTypography>
                  <CustomSubtitleTypography>접근 권한 수준을 선택하세요</CustomSubtitleTypography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Select fullWidth size="small" value={watch('accessLevel')} {...register('accessLevel')}>
                    {accessLevelList.map((v, idx) => (
                      <MenuItem key={`level-${idx}`} value={v.code}>
                        {v.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </CustomFormRow>
            </CustomSection>
          </Grid>
        </Grid>

        {/* ── 섹션 3: 수집 방법 (에디터) ── */}
        <CustomSection>
          <SectionTitle>수집 방법</SectionTitle>
          <ReactQuill
            modules={modules}
            theme="snow"
            style={{ height: '180px', borderRadius: '8px' }}
            value={watch('collectionMethod')}
            onChange={(v) => setValue('collectionMethod', v)}
          />
          <Box sx={{ height: '44px' }} />
        </CustomSection>

        {/* ── 섹션 4: 데이터 업로드 ── */}
        <CustomSection>
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadIcon />}
            style={{ height: '100%', textTransform: 'none', float: 'right' }}
            onClick={() => {
              window.open(
                `${process.env.REACT_APP_SERVER}${POST_SERVER}/metadata/template`,
                '_blank',
                'noopener,noreferrer'
              );
            }}
          >
            Metadata Template
          </Button>
          <SectionTitle>데이터 업로드</SectionTitle>

          <FileUploadContainer>
            {uploadProgresses.length > 0 && (
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle2" sx={{ color: '#3b82f6', mb: 1, fontWeight: 600, fontSize: '13px' }}>
                  임시 업로드 ({uploadProgresses.length}개)
                </Typography>
                <Stack spacing={0.75}>
                  {uploadProgresses.map((progress) => (
                    <FileItem key={progress.id}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, overflow: 'hidden' }}>
                          <AttachFileIcon sx={{ fontSize: 18, mr: 1, color: '#3b82f6', flexShrink: 0 }} />
                          <FileNameText title={progress.fileName}>{progress.fileName}</FileNameText>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeTempFile(progress.id)}
                          sx={{ p: 0.5, ml: 1, flexShrink: 0 }}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <ProgressContainer>
                        <LinearProgress
                          variant="determinate"
                          value={progress.progress}
                          color={
                            progress.status === 'done' ? 'success' : progress.status === 'error' ? 'error' : 'primary'
                          }
                          sx={{ flexGrow: 1, height: 6, borderRadius: '3px' }}
                        />
                        <Box
                          sx={{
                            minWidth: 36,
                            fontSize: '12px',
                            fontWeight: 600,
                            textAlign: 'center',
                            color:
                              progress.status === 'done'
                                ? '#16a34a'
                                : progress.status === 'error'
                                ? '#dc2626'
                                : '#3b82f6',
                          }}
                        >
                          {progress.status === 'done'
                            ? '완료'
                            : progress.status === 'error'
                            ? '오류'
                            : `${progress.progress}%`}
                        </Box>
                      </ProgressContainer>
                    </FileItem>
                  ))}
                </Stack>
              </Box>
            )}

            {/* 파일 추가 버튼 */}
            <Box
              sx={{
                pt: uploadProgresses.length ? 2 : 0,
                borderTop: uploadProgresses.length ? '1px dashed #d1d5db' : 'none',
                width: '100%',
                textAlign: 'center',
                mt: uploadProgresses.length ? 'auto' : 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flexGrow: uploadProgresses.length ? 0 : 1,
              }}
            >
              <IconButton
                component="label"
                size="large"
                sx={{
                  color: '#6b7280',
                  border: '2px dashed #d1d5db',
                  borderRadius: '50%',
                  width: 64,
                  height: 64,
                  '&:hover': { bgcolor: '#eff6ff', borderColor: '#3b82f6', color: '#3b82f6' },
                }}
              >
                <input type="file" multiple hidden ref={fileInput} onChange={handleFileInputChange} />
                <AddCircleOutlineIcon sx={{ fontSize: 28 }} />
              </IconButton>
              <Typography variant="body2" sx={{ mt: 1, color: '#6b7280', fontSize: '12px', fontWeight: 500 }}>
                클릭하여 파일 추가 (다중 선택 가능)
              </Typography>
            </Box>
          </FileUploadContainer>
        </CustomSection>

        {/* ── 하단 액션 버튼 ── */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, pt: 1 }}>
          <Button variant="outlined" onClick={() => navigate(-1)} sx={{ textTransform: 'none' }}>
            취소
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={handleSubmit(onSubmit)}
            disabled={isUploading}
            sx={{ textTransform: 'none', px: 3, height: '40px' }}
          >
            저장
          </Button>
        </Box>
      </Container>
    </div>
  );
};

export default AddPost;
